<script>
import useValidation from "@/use/Validation";
import useSharedData from "@/use/SharedData";
import useSharedMethod from "@/use/SharedMethod";
import useTools from "@/use/Tools";
import {useSchoolStore} from "@/stores/Training/School";
import {computed, onMounted, ref} from "vue";
import axios from "axios";

export default {
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        viewAction: Boolean,
        deleteAction: Boolean,
    },
    setup(props) {
        const readonly = computed(() => props.viewAction || props.deleteAction);
        const {mxRules} = useValidation();
        const {dialog, loading, overlay} = useSharedData();
        const {getBack} = useSharedMethod();
        const {$refs, $route} = useTools();
        const schoolStore = useSchoolStore();

        const school = ref({});

        function findById(id) {
            loading.value = true;
            axios.get(`/schools/${id}`).then((response) => {
                school.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }


        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.post('/schools', school.value).then((response) => {
                    schoolStore.addSchool(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.put('/schools', school.value).then((response) => {
                    schoolStore.updateSchool(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function remove() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.delete(`/schools/${school.value.id}`).then(() => {
                    schoolStore.deleteSchool(school.value);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        onMounted(() => {
            if (!props.addAction) {
                findById($route.params.id);
            }
        });

        return {
            schoolStore,
            mxRules,
            readonly,
            dialog,
            loading,
            overlay,
            school,
            save,
            update,
            remove,
            getBack,
        };
    }
}
</script>

<template>
    <v-dialog
        v-model="dialog"
        max-width="900"
        persistent
        scrollable
    >
        <v-card>
            <w-dialog-toolbar
                :view-action="viewAction"
                :delete-action="deleteAction"
                :add-action="addAction"
                :update-action="updateAction"
            ></w-dialog-toolbar>
            <w-overlay
                :overlay="overlay"
                :loading="loading"
                :delete-action="deleteAction"
                @remove="remove"
                @cancel="overlay = false"
            ></w-overlay>
            <v-card-text>
                <v-container grid-list-md>
                    <v-form ref="form">
                        <w-fieldset :title="$t('common.general')">
                            <v-row dense>
                                <v-col cols="12">
                                    <w-label :label="$t('common.name')" required/>
                                    <w-text-field
                                        v-model="school.name"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <w-label :label="$t('common.address')" required/>
                                    <w-text-field
                                        v-model="school.address"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="6">
                                    <w-label :label="$t('common.country_state')" required/>
                                    <w-state-autocomplete
                                        v-model="school.cityStateId"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="6">
                                    <w-label :label="$t('common.country_city')" required/>
                                    <w-city-autocomplete
                                        v-model="school.cityId"
                                        item-value="id"
                                        :state-id="school.cityStateId"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="6">
                                    <w-label :label="$t('common.phone')"/>
                                    <w-text-field
                                        v-model="school.phone"
                                        :readonly="readonly"
                                    />
                                </v-col>
                                <v-col cols="6">
                                    <w-label :label="$t('common.fax')"/>
                                    <w-text-field
                                        v-model="school.fax"
                                        :readonly="readonly"
                                    />
                                </v-col>
                                <v-col cols="6">
                                    <w-label :label="$t('common.email')"/>
                                    <w-text-field
                                        v-model="school.email"
                                        :readonly="readonly"
                                    />
                                </v-col>
                                <v-col cols="6">
                                    <w-label :label="$t('common.web_site')"/>
                                    <w-text-field
                                        v-model="school.webSite"
                                        :readonly="readonly"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                    </v-form>
                </v-container>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add
                    :show="addAction"
                    :loading="loading"
                    @press="save(false)"
                />
                <w-btn-update
                    :show="updateAction"
                    :loading="loading"
                    @press="update"
                />
                <w-btn-delete
                    :show="deleteAction"
                    :loading="loading"
                    @press="overlay = true"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>